import { Chip, TextField } from "@mui/material";
import { ChangeEvent, ReactElement } from "react";

import UserRole from "../../components/user-role/user-role";
import { Asset, UpdateAssetInput } from "../../generated/gql/graphql";
import useText from "../../hooks/use-text";
import Title from "../../pages/detail-page/parts/title";
import { getLatestDate } from "../../utils/date";
import { LocalizedStrings, SupportedLanguages } from "../../utils/lang";

import AssetMetadata from "./asset-metadata";
import CategorySelector from "./category-selector";

type Props = {
  asset: Asset;
  isEdit: boolean;
  update: UpdateAssetInput | undefined;

  onChange(event: ChangeEvent<HTMLInputElement>): void;
};

export default function AssetDetails(props: Props): ReactElement {
  const { asset, isEdit, update, onChange } = props;

  const { strings } = useText(local);

  return (
    <div
      // className={`${messageNavigation ? "w-1/3" : "w-full"}`}
      className="w-full"
    >
      <h2 className="text-[24px] font-bold mb-[40px] text-slate-600">
        {strings.header}
      </h2>

      <div className="text-16 mb-[40px] break-all">
        <Title>{strings.details.name}</Title>
        <p>{asset.name}</p>
      </div>

      <div className="text-16 mb-[40px] break-all">
        <Title>{strings.details.uploadDate}</Title>
        {asset &&
          asset.dateCreated &&
          getLatestDate(asset).toLocaleDateString()}
      </div>

      <div className="text-16 mb-[40px] break-all">
        <Title>{strings.details.masterId}</Title>
        {isEdit ? (
          <TextField
            defaultValue={update?.master_id ?? asset.masterId}
            size="small"
            name="master_id"
            onChange={onChange}
          />
        ) : asset.masterId ? (
          <p>MAS-{asset.masterId}</p>
        ) : (
          strings.details.status.empty
        )}
      </div>

      <div className="text-16 mb-[40px] break-all">
        <Title>{strings.details.projectId}</Title>
        {isEdit ? (
          <TextField
            defaultValue={update?.parent_id ?? asset.projectId}
            size="small"
            name="project_id"
            onChange={onChange}
          />
        ) : asset.projectId ? (
          <p>P-{asset.projectId}</p>
        ) : (
          strings.details.status.empty
        )}
      </div>

      <div className="text-16 mb-[40px] break-all">
        <Title>{strings.details.adaptionId}</Title>
        {isEdit ? (
          <TextField
            defaultValue={update?.adaption_id ?? asset.adaptionId}
            size="small"
            name="adaption_id"
            onChange={onChange}
          />
        ) : asset.adaptionId ? (
          <p>AD-ID-{asset.adaptionId}</p>
        ) : (
          strings.details.status.empty
        )}
      </div>

      <div className="text-16 mb-[40px]">
        <Title>{strings.details.status.label}</Title>
        {asset.status ? (
          <Chip label={asset?.status.toUpperCase()} />
        ) : (
          strings.details.status.empty
        )}
      </div>

      <div className="text-16 mb-[40px]">
        <Title>{strings.details.assetType.label}</Title>
        {asset.assetTypes ? (
          <Chip label={asset?.assetTypes.toUpperCase()} />
        ) : (
          strings.details.assetType.empty
        )}
      </div>

      <div className="text-16 mb-[40px]">
        <Title>{strings.details.productType.label}</Title>
        {asset.productType ? (
          <Chip label={asset?.productType.name.toUpperCase()} />
        ) : (
          strings.details.productType.empty
        )}
      </div>

      <div className="text-16 mb-[40px]">
        <Title>{strings.details.labelType.label}</Title>
        {asset.labelType ? (
          <Chip label={asset?.labelType.name.toUpperCase()} />
        ) : (
          strings.details.labelType.empty
        )}
      </div>

      <div className="text-16 mb-[40px]">
        <Title>{strings.details.typeOfShot.label}</Title>
        {asset.typeOfShot ? (
          <Chip label={asset?.typeOfShot.name.toUpperCase()} />
        ) : (
          strings.details.typeOfShot.empty
        )}
      </div>

      <UserRole>
        <CategorySelector
          selectedCategoryId={asset.categoryId || ""}
          assetId={asset.id || ""}
        />
      </UserRole>

      <AssetMetadata asset={asset} />
    </div>
  );
}

type LocalSchema = {
  header: string;
  details: {
    name: string;
    uploadDate: string;
    masterId: string;
    projectId: string;
    adaptionId: string;
    status: {
      label: string;
      empty: string;
    };
    assetType: {
      label: string;
      empty: string;
    };
    productType: {
      label: string;
      empty: string;
    };
    labelType: {
      label: string;
      empty: string;
    };
    typeOfShot: {
      label: string;
      empty: string;
    };
  };
};

const local: LocalizedStrings<LocalSchema, SupportedLanguages> = {
  default: {
    header: "Asset Details",
    details: {
      name: "Name",
      uploadDate: "Date Uploaded",
      masterId: "Master ID",
      projectId: "Project ID",
      adaptionId: "Adaption ID",
      status: {
        label: "Asset Status",
        empty: "None",
      },
      assetType: {
        label: "Asset Type",
        empty: "None",
      },
      productType: {
        label: "Product Type",
        empty: "None",
      },
      labelType: {
        label: "Label Type",
        empty: "None",
      },
      typeOfShot: {
        label: "Type of Shot",
        empty: "None",
      },
    },
  },
};
